// Service hook (TS-2.5.01, Resource Accessor layer) - the archive-browsing read path (BRD
// section 8.2: "see the deck as it stood"). GET /api/roadmap/versions/{versionKey} returns the
// same RoadmapView shape the live board uses, frozen at that version's snapshot (decisions.md
// D-003), so this hook is a version-scoped sibling of useRoadmap rather than a new read path.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../../api/ApiError";
import type { RoadmapView } from "../../../api/generated/contracts";
import { roadmapApi } from "../../../api/roadmap";

export interface UseVersionRoadmapResult {
  readonly view: RoadmapView | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
}

export function useVersionRoadmap(versionKey: string | null): UseVersionRoadmapResult {
  const [view, setView] = useState<RoadmapView | null>(null);
  const [loading, setLoading] = useState(versionKey !== null);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!versionKey) {
      setView(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    void roadmapApi
      .getVersion(versionKey)
      .then((next) => {
        setView(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The archived roadmap could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [versionKey]);

  // A frozen snapshot never changes once published (D-003), so unlike useRoadmap this does not
  // re-subscribe to push-channel invalidation - there is nothing live here to invalidate.
  useEffect(refresh, [refresh]);

  return { view, loading, error };
}
